import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function RecipeDriftPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [snapBusy, setSnapBusy] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/insights/recipe-drift`, { withCredentials: true });
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load drift'); }
    finally { setLoading(false); }
  };

  React.useEffect(() => { load(); }, []);

  const snapshotAll = async () => {
    if (!window.confirm('Reset the cost baseline for every recipe to its current cost/kg? Future drift will be measured from now on.')) return;
    setSnapBusy(true);
    try {
      const res = await axios.post(`${API}/insights/recipe-drift/snapshot`, {}, { withCredentials: true });
      toast.success(`Snapshotted ${res.data.recipes_updated} recipes`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Snapshot failed'); }
    finally { setSnapBusy(false); }
  };

  const snapshotOne = async (pid, name) => {
    if (!window.confirm(`Reset baseline for "${name}"?`)) return;
    try {
      await axios.post(`${API}/insights/recipe-drift/snapshot/${pid}`, {}, { withCredentials: true });
      toast.success('Baseline updated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="text-zinc-400 text-sm py-6">Loading drift analysis…</div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="drift-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-zinc-50 font-heading font-semibold text-sm flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-400" /> Recipe Cost Drift
          </h3>
          <p className="text-zinc-500 text-xs mt-0.5">{data.total} recipes · <span className="text-red-400 font-mono">{data.flagged_count}</span> flagged · threshold ±{data.threshold_pct}%</p>
        </div>
        <button onClick={snapshotAll} disabled={snapBusy} className="text-xs px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 text-black font-semibold flex items-center gap-1.5" data-testid="drift-snapshot-all">
          <RefreshCw size={12} /> {snapBusy ? 'Snapshotting…' : 'Reset all baselines'}
        </button>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-zinc-500 text-xs">
            <tr>
              <th className="text-left px-4 py-2">Product</th>
              <th className="text-right px-4 py-2">Snapshot ₹/kg</th>
              <th className="text-right px-4 py-2">Current ₹/kg</th>
              <th className="text-right px-4 py-2">Drift</th>
              <th className="text-left px-4 py-2">Spiked Ingredients</th>
              <th className="text-right px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {data.recipes.map(r => (
              <tr key={r.product_id} className={`hover:bg-zinc-800/40 ${r.flagged ? 'bg-red-900/10' : ''}`} data-testid={`drift-row-${r.product_id}`}>
                <td className="px-4 py-2 text-zinc-200">{r.product_name} {r.product_code && <span className="text-zinc-600 text-xs ml-1">· {r.product_code}</span>}</td>
                <td className="px-4 py-2 text-right font-mono text-zinc-400">{r.snapshot_cost_per_kg > 0 ? `₹${r.snapshot_cost_per_kg}` : '—'}</td>
                <td className="px-4 py-2 text-right font-mono text-amber-300">₹{r.current_cost_per_kg}</td>
                <td className="px-4 py-2 text-right font-mono">
                  {r.drift_pct === null ? <span className="text-zinc-600">—</span> : (
                    <span className={r.flagged ? 'text-red-300 font-semibold' : 'text-zinc-400'}>
                      {r.drift_pct > 0 ? '+' : ''}{r.drift_pct}%
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-xs">
                  {r.spiked_ingredients?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {r.spiked_ingredients.slice(0, 3).map(ing => (
                        <span key={ing.material_name} className={`px-1.5 py-0.5 rounded border ${ing.drift_pct > 0 ? 'bg-red-900/30 text-red-300 border-red-700' : 'bg-green-900/30 text-green-300 border-green-700'}`}>
                          {ing.material_name} {ing.drift_pct > 0 ? '+' : ''}{ing.drift_pct}%
                        </span>
                      ))}
                      {r.spiked_ingredients.length > 3 && <span className="text-zinc-500">+{r.spiked_ingredients.length - 3} more</span>}
                    </div>
                  ) : <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => snapshotOne(r.product_id, r.product_name)} className="text-xs text-amber-400 hover:text-amber-300" title="Reset baseline for this recipe" data-testid={`drift-snapshot-${r.product_id}`}>
                    Accept
                  </button>
                </td>
              </tr>
            ))}
            {data.recipes.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 py-6 text-xs">No recipes yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
